// Today — the focused post-login view. Detailed learning, paper discovery and
// mastery management remain available in their dedicated sections.
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api';
import { decodeClaims } from '@/lib/auth';
import type { BinColor, DailyStudyPlanDTO, TaskCardDTO } from '@/types';
import { useTaskCards, studentId } from '@/hooks/useTaskCards';
import { useGarden, weekActivity, DAILY_GOAL } from '@/hooks/useGarden';
import { Card, Button, StudentPage, StudentPageHeader } from '@/components/ds';
import { TodayHero, MasterySackGrid, Ring, WeekStrip, Icon } from '@/components/garden';
import { StickyQuote } from '@/components/StickyQuote';
import { HomeworkBanner } from '@/components/dashboard/HomeworkFromTeacher';
import { AssignedPapersBanner } from '@/components/dashboard/AssignedPapersBanner';

const REFERENCE_PAPER_ID = 'paper_cbse10_mock1';

export function DashboardPage() {
  const claims = useMemo(() => decodeClaims(), []);
  const sid = studentId();
  const navigate = useNavigate();
  const { state: garden } = useGarden();
  const { data: cards } = useTaskCards();

  const studyPlanQuery = useQuery({
    queryKey: ['study-plan', sid, claims?.board, claims?.grade],
    enabled: Boolean(sid && claims?.board && claims?.grade),
    queryFn: () =>
      apiGet<DailyStudyPlanDTO>(
        `/dashboard/${sid}/study-plan?board=${claims!.board}&grade=${claims!.grade}`,
      ),
  });

  const byBin = useMemo(() => {
    const map: Record<BinColor, TaskCardDTO[]> = {
      GREEN: [],
      PURPLE: [],
      BLUE: [],
      ORANGE: [],
      PINK: [],
    };
    for (const c of cards ?? []) map[c.currentBin].push(c);
    return map;
  }, [cards]);

  const dueCount = useMemo(() => {
    const horizon = Date.now() + 24 * 3.6e6;
    return (cards ?? []).filter((c) => new Date(c.dueDate).getTime() <= horizon).length;
  }, [cards]);

  // Progress-derived display values.
  const goalPct = Math.min(100, Math.round((garden.todayXP / DAILY_GOAL) * 100));
  const week = weekActivity(garden);
  const greenCount = byBin.GREEN.length;
  const blueReady = byBin.BLUE.length;
  const totalCards = (cards ?? []).length;
  const pctGrown = totalCards > 0 ? Math.round((greenCount / totalCards) * 100) : 0;
  const firstName = claims?.name?.split(' ')[0] || claims?.email?.split('@')[0] || 'there';
  // Today's set = the real personalized study plan (src/jobs/studyPlan.ts)
  // when it's loaded; a sane placeholder while it's still fetching/missing so
  // the card never looks broken.
  const studyPlan = studyPlanQuery.data;
  const setSize = studyPlan ? studyPlan.items.length : Math.max(dueCount, 8);
  const setMinutes = studyPlan
    ? Math.round(studyPlan.estTotalSeconds / 60)
    : Math.round(setSize * 1.5);

  const startExam = useMutation({
    mutationFn: () => apiPost<{ id: string }>('/student-papers', { paperId: REFERENCE_PAPER_ID }),
    onSuccess: (sp) => navigate(`/exam/${sp.id}`),
  });

  return (
    <StudentPage>
      {/* ── Context strip ── */}
      {/* Streak + notifications now live in GardenShell's persistent top strip;
          this keeps only page-specific context. */}
      <StudentPageHeader
        title="Today"
        subtitle="Clear today’s set to keep your streak."
        action={
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold"
            style={{
              background: 'var(--blue-bg)',
              color: 'var(--blue-text)',
              border: '1px solid var(--blue-fill)',
            }}
          >
            <Icon name="droplet" size={14} /> <span data-numeric="">{dueCount}</span> due
          </span>
        }
      />

      {/* A paper a teacher set that is not started yet. Nothing until one is. */}
      <AssignedPapersBanner />

      {/* Homework a teacher sent. Renders nothing until one has. */}
      <HomeworkBanner />

      <Card title="Where your concepts stand">
        <MasterySackGrid byBin={byBin} />
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => navigate('/progress')}>
            View progress
          </Button>
          <Button size="sm" variant="ghost" onClick={() => navigate('/practice/cards')}>
            Review cards
          </Button>
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-[1.5fr_1fr]">
        {/* ── Main column ── */}
        <div className="flex flex-col gap-5">
          <TodayHero
            name={firstName}
            pctMastered={pctGrown}
            masteredCount={greenCount}
            totalCount={totalCards}
            doneToday={garden.todayXP}
            goalToday={DAILY_GOAL}
          />

          {/* Today's set (the real personalized study plan from
              src/jobs/studyPlan.ts when loaded, ranked reviews-due-today
              first, then weakest topics). */}
          <div
            className="relative overflow-hidden rounded-[22px] p-6"
            style={{ background: 'var(--brand-forest)' }}
          >
            <div
              className="text-fs-11 font-bold uppercase tracking-wide"
              style={{ color: 'var(--brand-lime)' }}
            >
              Today's set
            </div>
            <div className="mt-2 text-fs-24 font-extrabold leading-tight text-white">
              Practise {setSize} cards
            </div>
            <div
              className="mt-1 text-fs-13.5 font-medium"
              style={{ color: '#b9d197' }}
              data-numeric=""
            >
              {studyPlan && studyPlan.focusTopics.length > 0
                ? studyPlan.focusTopics.join(' · ')
                : 'Mixed review'}{' '}
              · ~{setMinutes} min
            </div>
            <div className="mt-5">
              <Button
                variant="accent"
                onClick={() => startExam.mutate()}
                disabled={startExam.isPending}
                iconLeft={<Icon name="droplet" size={15} />}
              >
                {startExam.isPending ? 'Starting…' : 'Start today’s set'}
              </Button>
              {startExam.isError && (
                <p className="mt-2 text-xs" style={{ color: 'var(--pink-fill)' }}>
                  Couldn’t start. Please try again.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Rail ── */}
        <div className="flex flex-col gap-4">
          <Card>
            <div className="flex flex-col items-center text-center">
              <div className="text-fs-11 font-bold uppercase tracking-wide text-ink-faint">
                Daily goal
              </div>
              <div className="mt-2">
                <Ring
                  pct={goalPct}
                  big={String(garden.todayXP)}
                  sm={`of ${DAILY_GOAL}`}
                  tone="amber"
                />
              </div>
              <div className="mt-1.5 text-xs text-ink-faint" data-numeric="">
                {garden.todayXP} of {DAILY_GOAL} done today
              </div>
            </div>
          </Card>

          <Card title="This week">
            <WeekStrip active={week} />
          </Card>

          {/* Today's nudge — square tile, right under the daily goal, next to
              the Today hero card in the grid's first row. */}
          <StickyQuote progress={{ streak: garden.streak, dueCount, greenCount }} />

          <Card title="Go-Green nudge" info="goGreen">
            <div className="flex items-center justify-between gap-3 rounded-md border border-line bg-paper px-3 py-2">
              <span className="text-sm text-ink-soft">
                {blueReady > 0
                  ? `${blueReady} Blue card${blueReady > 1 ? 's are' : ' is'} one step from Green`
                  : 'No cards waiting — nice and tidy'}
              </span>
              {blueReady > 0 && (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold"
                  style={{ background: 'var(--blue-bg)', color: 'var(--blue-text)' }}
                >
                  <Icon name="arrow-up" size={12} /> <span data-numeric="">{blueReady}</span>
                </span>
              )}
            </div>
          </Card>

          <Card title="This week’s best" info="weeksBest">
            <div className="flex items-center justify-between">
              <span className="text-sm text-ink-soft">Concepts moved to Green</span>
              <span
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold"
                style={{ background: 'var(--green-bg)', color: 'var(--green-text)' }}
              >
                <Icon name="sprout" size={12} /> <span data-numeric="">{greenCount}</span>
              </span>
            </div>
          </Card>
        </div>
      </div>
    </StudentPage>
  );
}
